import { useEffect, useMemo, useState } from "react";
import { AlertCircle, AlertTriangle, Search } from "lucide-react";
import { mutate } from "swr";
import { Checkbox } from "~/components/ui/checkbox";
import { Input } from "~/components/ui/input";
import useUser from "~/contexts/user";
import { useAgentList } from "~/hooks/useAgentList";
import { useConversationsList } from "~/hooks/useConversationsList";
import { useFetcher } from "~/hooks/useFetcher";
import { stripMarkdownToText } from "~/lib/stripMarkdownToText";
import { cn } from "~/lib/utils";
import { FormDialog } from "~/pages/manifold-nextgen/components/FormDialog";
import { AgentAvatar } from "~/pages/manifold-nextgen/components/agents/AgentAvatar";
import { CopyAllEmailsButton } from "~/pages/manifold-nextgen/components/agents/CopyAllEmailsButton";
import {
  listMembers,
  memberCountPhrase,
  toolkitPhrase,
} from "~/pages/manifold-nextgen/components/agents/toolkitAccessCopy";
import { useAsyncMutation } from "~/pages/manifold-nextgen/hooks/useAsyncMutation";
import { Agent, AgentStatus, AgentToolkit, agentToolkitsPath } from "~/services/agents";
import {
  type ProjectAgentToolkitAccess,
  projectAgentToolkitAccessPath,
  projectAgentsPath,
  projectMyResourceAccessPath,
  setProjectAgents,
} from "~/services/projects";

interface ProjectAgentsDialogProps {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  attached: Agent[];
}

export const ProjectAgentsDialog = ({
  projectId,
  open,
  onOpenChange,
  attached,
}: ProjectAgentsDialogProps) => {
  const { data, error, isLoading } = useAgentList(open);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (open) {
      setSelected(new Set());
      setSearch("");
    }
  }, [open]);

  const attachedIds = useMemo(() => new Set(attached.map((a) => a.id)), [attached]);

  const filtered = useMemo(() => {
    const items = (data?.items ?? []).filter(
      (a) => !attachedIds.has(a.id) && a.status !== AgentStatus.DISABLED
    );
    const q = search.trim().toLowerCase();
    if (!q) {
      return items;
    }
    return items.filter(
      (a) => a.display_name.toLowerCase().includes(q) || a.description.toLowerCase().includes(q)
    );
  }, [data?.items, attachedIds, search]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const { execute, isPending } = useAsyncMutation(
    (ids: string[]) => setProjectAgents(projectId, ids),
    {
      errorMessage: "Failed to add agents",
      onSuccess: async () => {
        await Promise.all([
          mutate(projectAgentsPath(projectId)),
          mutate(projectMyResourceAccessPath(projectId)),
        ]);
        onOpenChange(false);
      },
    }
  );

  const handleSave = () => {
    const newIds = [...attached.map((a) => a.id), ...Array.from(selected)];
    void execute(newIds);
  };

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Add Agents"
      description="Select agents to add to this project."
      contentClassName="sm:max-w-[640px]"
      onPrimary={handleSave}
      isPending={isPending}
      primaryLabel="Add"
      primaryPendingLabel="Adding…"
      primaryDisabled={selected.size === 0}
    >
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search agents..."
          aria-label="Search agents"
          className="pl-9"
        />
      </div>

      <div className="-mx-2 flex h-[50vh] flex-col overflow-y-auto">
        {isLoading && (
          <div role="status" className="flex flex-col gap-2 px-2 py-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-14 animate-pulse rounded-md bg-muted" />
            ))}
          </div>
        )}

        {!isLoading && error && (
          <div
            role="alert"
            className="flex flex-col items-center gap-2 px-2 py-8 text-center text-destructive"
          >
            <AlertCircle className="size-6" aria-hidden="true" />
            <p className="text-sm font-medium">Failed to load agents</p>
          </div>
        )}

        {!isLoading && !error && filtered.length === 0 && (
          <p className="px-2 py-8 text-center text-sm text-muted-foreground">
            {search ? "No agents match your search." : "No more agents to add."}
          </p>
        )}

        {!isLoading && !error && filtered.length > 0 && (
          <ul className="flex flex-col" role="list">
            {filtered.map((agent) => (
              <AgentRow
                key={agent.id}
                projectId={projectId}
                agent={agent}
                checked={selected.has(agent.id)}
                onToggle={() => toggle(agent.id)}
              />
            ))}
          </ul>
        )}
      </div>
    </FormDialog>
  );
};

interface AgentRowProps {
  projectId: string;
  agent: Agent;
  checked: boolean;
  onToggle: () => void;
}

const AgentRow = ({ projectId, agent, checked, onToggle }: AgentRowProps) => {
  const checkboxId = `agent-row-${agent.id}`;
  const user = useUser();

  // An agent's toolkits aren't in the list payload, so fetch them only once the
  // row is selected (the point at which the user is about to add it). Each
  // toolkit carries its required scopes, so a toolkit whose scopes the user
  // lacks becomes an add-time warning.
  const { data: agentToolkits } = useConversationsList<AgentToolkit>(
    checked ? agentToolkitsPath(agent.id) : null
  );
  const permissions = new Set(user?.permissions ?? []);
  const inaccessibleToolkitNames = (agentToolkits ?? [])
    .filter((t) => t.toolkit.required_scopes.some((scope) => !permissions.has(scope)))
    .map((t) => t.toolkit.display_name);
  const scopedToolkitNames = (agentToolkits ?? [])
    .filter((t) => t.toolkit.required_scopes.length > 0)
    .map((t) => t.toolkit.display_name);

  // Check the project's members once the row is selected: they'll see the agent
  // in the project, but project access doesn't share it with them, and those
  // it is shared with may lack a toolkit's scopes. Members' toolkit gaps are
  // only reported once the user can use the toolkits themselves.
  const { data: memberAccess } = useFetcher<ProjectAgentToolkitAccess>(
    checked ? projectAgentToolkitAccessPath(projectId, agent.id) : null
  );
  const memberWarnings = memberAccess
    ? memberWarningsFor(memberAccess, {
        scopedToolkitNames,
        includeToolkitGaps: inaccessibleToolkitNames.length === 0,
      })
    : [];

  return (
    <li>
      <label
        htmlFor={checkboxId}
        className={cn(
          "flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 transition-colors",
          "hover:bg-accent/40"
        )}
      >
        <AgentAvatar agent={agent} size={32} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-foreground">{agent.display_name}</p>
          {agent.description && (
            <p className="truncate text-xs text-muted-foreground">
              {stripMarkdownToText(agent.description)}
            </p>
          )}
        </div>
        <Checkbox id={checkboxId} checked={checked} onCheckedChange={onToggle} />
      </label>
      {/* Kept out of the label's centered row so the avatar/name/checkbox stay
       * vertically aligned; indented to line up under the agent's name. */}
      {inaccessibleToolkitNames.length > 0 && (
        <p className="mb-1 flex items-start gap-1 pl-11 pr-2 text-xs text-amber-600">
          <AlertTriangle className="mt-0.5 size-3 shrink-0" aria-hidden="true" />
          <span>
            Uses the {inaccessibleToolkitNames.join(", ")}{" "}
            {inaccessibleToolkitNames.length > 1 ? "toolkits" : "toolkit"}, which you don&apos;t
            have access to. Please reach out to an admin for access.
          </span>
        </p>
      )}
      {memberWarnings.map(({ text, lackingEmails }) => (
        <p key={text} className="mb-1 flex items-start gap-1 pl-11 pr-2 text-xs text-amber-600">
          <AlertTriangle className="mt-0.5 size-3 shrink-0" aria-hidden="true" />
          <span>{text}</span>
          {lackingEmails && <CopyAllEmailsButton emails={lackingEmails} />}
        </p>
      ))}
    </li>
  );
};

interface MemberWarning {
  text: string;
  // The members the warning is about, when it names them.
  lackingEmails?: string[];
}

function memberWarningsFor(
  access: ProjectAgentToolkitAccess,
  {
    scopedToolkitNames,
    includeToolkitGaps,
  }: { scopedToolkitNames: string[]; includeToolkitGaps: boolean }
): MemberWarning[] {
  const warnings: MemberWarning[] = [];
  const without = access.members_without_agent_access;
  if (without.length > 0) {
    const who = memberCountPhrase(without.length, access.member_count, "project member");
    warnings.push({
      text: `${who} (${listMembers(without)}) won't be able to use this agent, which isn't shared with them. Please reach out to the agent's owner to grant them access.`,
      lackingEmails: without,
    });
  }
  if (!includeToolkitGaps) {
    return warnings;
  }
  const lacking = access.members_lacking_access;
  if (lacking.length > 0) {
    const who = memberCountPhrase(lacking.length, access.member_count, "project member");
    const toolkits = toolkitPhrase(access.toolkits.map((t) => t.display_name));
    warnings.push({
      text: `${who} (${listMembers(lacking)}) won't be able to use this agent's ${toolkits}. Please reach out to an admin to grant them access.`,
      lackingEmails: lacking,
    });
  }
  // Everyone can't be checked member by member, so this is a generic note
  // about every toolkit that needs a scope.
  if (access.includes_everyone && scopedToolkitNames.length > 0) {
    warnings.push({
      text: `This project is shared with everyone, so some users may not be able to use this agent's ${toolkitPhrase(scopedToolkitNames)}.`,
    });
  }
  return warnings;
}
